// Image metadata for the Mission Map (city panel + mission briefing).
//
// DATA ONLY — UI components ask resolveCityImage() / resolveMissionImage() for a
// ready-to-render { src, alt, credit } and never read licensing fields themselves.
// To replace an image, drop a new file at the same path, or change `image` here;
// no logic changes are needed.
//
// sourceType:
//   'generated' — original illustration produced by scripts/build-illustrations.mjs
//                 (no third-party material; attribution: null)
//   'project'   — an asset that already belongs to this project
//   'external'  — a third-party image (e.g. Wikimedia Commons). Allowed ONLY when the
//                 individual file's licence permits it, and it MUST carry source,
//                 author, license, attribution and sourceURL copied from the file's
//                 own licence page. Entries that miss any of these are rejected and the
//                 UI falls back to the placeholder. Never invent attribution, and never
//                 use search-engine image results, map screenshots or map tiles.
//
// No external images are used at present: every entry below is 'generated'.

export const CITY_IMAGES = {
  d01: { cityId: 'city-manila', image: 'images/cities/manila.svg', alt: 'Stylised illustration of a bayside city skyline at sunset with a tall headquarters tower', sourceType: 'generated', attribution: null },
  d02: { cityId: 'city-cebu', image: 'images/cities/cebu.svg', alt: 'Stylised illustration of a harbour city with green hills and a long arched bridge', sourceType: 'generated', attribution: null },
  d03: { cityId: 'city-davao', image: 'images/cities/davao.svg', alt: 'Stylised illustration of a city at the foot of a tall volcano, with a communications tower and a gulf', sourceType: 'generated', attribution: null },
  d04: { cityId: 'city-batangas', image: 'images/cities/batangas.svg', alt: 'Stylised illustration of a port city with cargo cranes, ships and a tall communications tower with microwave dishes', sourceType: 'generated', attribution: null },
  d05: { cityId: 'city-clark', image: 'images/cities/clark.svg', alt: 'Stylised illustration of a data-center hall with rooftop cooling units and an antenna mast, beside an airfield runway and a mountain', sourceType: 'generated', attribution: null },
  d06: { cityId: 'city-taguig', image: 'images/cities/taguig.svg', alt: 'Stylised illustration of a modern business district with glass towers and parks', sourceType: 'generated', attribution: null },
  d07: { cityId: 'city-iloilo', image: 'images/cities/iloilo.svg', alt: 'Stylised illustration of a riverside city with a heritage hall and colonnade', sourceType: 'generated', attribution: null },
};

const KIND_ALT = {
  LEARN: 'a lesson whiteboard',
  LAB: 'an equipment rack with patch cables',
  TROUBLESHOOTING: 'an operations console and a magnifying glass',
  CHALLENGE: 'an incident wall of monitoring screens with a warning sign',
};

/**
 * Mission images. Generic scenes by mission kind, tinted per domain. They show the
 * TYPE of work only and never depict device state, so they cannot reveal a fault.
 * `MISSION_OVERRIDES['d03.u14'] = {...}` would give a single mission its own image.
 */
export const MISSION_IMAGES = Object.fromEntries(['d01', 'd02', 'd03', 'd04', 'd05', 'd06', 'd07'].flatMap((d) =>
  Object.entries(KIND_ALT).map(([kind, what]) => [`${d}.${kind}`, { image: `images/missions/${d}-${kind.toLowerCase()}.svg`, alt: `Stylised illustration: ${what}`, sourceType: 'generated', attribution: null }])));
export const MISSION_OVERRIDES = {};

const EXTERNAL_FIELDS = ['source', 'author', 'license', 'attribution', 'sourceURL'];

/** Returns null when an entry may be shown, otherwise the reason it is rejected. */
export function imageProblem(meta) {
  if (!meta || typeof meta !== 'object') return 'no image metadata';
  if (!meta.image || typeof meta.image !== 'string') return 'no image path';
  if (!meta.alt) return 'missing alt text';
  if (meta.sourceType === 'generated' || meta.sourceType === 'project') return null;
  if (meta.sourceType === 'external') {
    const missing = EXTERNAL_FIELDS.filter((k) => !meta[k] || typeof meta[k] !== 'string');
    if (missing.length) return `external image missing ${missing.join(', ')}`;
    if (!/^https:\/\//.test(meta.sourceURL)) return 'external image sourceURL must be https';
    return null;
  }
  return `unknown sourceType "${meta.sourceType}"`;
}

// The single-file build inlines the illustrations as data: URIs (scripts/build-single.mjs).
const inlined = (p) => (typeof globalThis !== 'undefined' && globalThis.__NELL_IMAGES__?.[p]) || p;

function resolve(meta, fallbackAlt) {
  const problem = imageProblem(meta);
  if (problem) return { src: null, alt: fallbackAlt, credit: null, fallback: true, problem };
  const credit = meta.sourceType === 'external'
    ? `${meta.attribution} · ${meta.license}`
    : meta.sourceType === 'generated' ? 'Original illustration' : null;
  return { src: inlined(meta.image), alt: meta.alt, credit, creditURL: meta.sourceType === 'external' ? meta.sourceURL : null, fallback: false };
}

export function resolveCityImage(domainId, table = CITY_IMAGES) {
  return resolve(table[domainId], 'Illustration unavailable');
}
export function resolveMissionImage(domainId, unit, kind, { overrides = MISSION_OVERRIDES, table = MISSION_IMAGES } = {}) {
  return resolve(overrides[`${domainId}.${unit.id}`] || table[`${domainId}.${kind}`], 'Mission illustration unavailable');
}
